import { FormBlock } from '@qbe/ads-blocks-forms';
import {
  Alert,
  ButtonGroup,
  Form,
  FormGroupLabel,
  Spacer,
  Spinner,
  TextField,
  Typography,
} from '@qbe/ads-core';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';

import { useAnalytics, useAppContent, useAppDispatch, usePayMyBill } from '~/hooks';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { updateRenewalPaymentId } from '~/slices/analytics/trackAnalyticsSlice';
import { PayMyBillPolicy, updatePolicy } from '~/slices/pay-my-bill/slice';
import { retrievePolicyAction } from '~/slices/pay-my-bill/thunks';
import { qalTrackTransactionStartAction } from '~/thunks/analytics';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';
import { PaymentState } from './types';

interface FormData {
  reference: string;
}

interface RetrievePolicyDetailsFormProps {
  paymentState: PaymentState;
  onComplete: (success: boolean) => void;
  onExit: () => void;
}

const ErrorMessage: React.FC<{ policy: PayMyBillPolicy | undefined }> = ({ policy }) => {
  const { getJSXContent } = useAppContent({
    basePath: 'dsme.payMyBill',
  });
  let message = getJSXContent('content.errorMessageRenewalAssistance.description');

  if (!policy) return null;
  if (policy.policyStatus === '04') {
    if (policy.policyType !== 'installment') {
      return null;
    }
    message = getJSXContent('content.errorMessagePolicyRenewal.description');
  }
  if (policy.policyStatus === '01') {
    message = getJSXContent('content.errorMessageNoPaymentExpected.description');
  }

  return (
    <>
      <Spacer size="sm" />
      <Alert heading="" variant="error">
        {message}
      </Alert>
    </>
  );
};

export const RetrievePolicyDetailsForm = ({
  paymentState,
  onComplete,
  onExit,
}: RetrievePolicyDetailsFormProps) => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });

  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.payMyBill',
    tokens: {
      policyNumber: () => policyNumber ?? '',
    },
  });

  const { policy, policyNotFound } = usePayMyBill();
  const [policyNumber, setPolicyNumber] = useState<string>();
  const [hasError, setHasError] = useState(false);

  const { handleSubmit, formState, control } = useFormWithAnalytics<FormData>({
    mode: 'onChange',
    defaultValues: {
      reference: '',
    },
  });
  const { goalIdentifiers } = useAnalytics();
  const { renewalPaymentId } = goalIdentifiers;

  const onSubmit = handleSubmit(async (data: FormData | undefined) => {
    if (!data || !data.reference) {
      console.error('Form data is incomplete');
      return;
    }
    try {
      setPolicyNumber(undefined);
      dispatch(updatePolicy(undefined));
      if (renewalPaymentId === '') {
        dispatch(qalTrackTransactionStartAction(data.reference));
      }
      setPolicyNumber(data.reference.toUpperCase());
      dispatch(updateRenewalPaymentId({ renewalPaymentId: data.reference }));
      const policyResponse = await dispatch(
        retrievePolicyAction(data.reference.toUpperCase())
      ).unwrap();
      if (
        policyResponse?.policyStatus === '04' &&
        policyResponse.policyType === 'non-installment'
      ) {
        onComplete(true);
      } else {
        onComplete(false);
      }
    } catch (e) {
      setHasError(true);
    }
  });

  const dispatch = useAppDispatch();

  return (
    <Form onSubmit={onSubmit}>
      <FormGroupLabel
        title={getTextContent('fields.policyNumber.title')}
        alignCenter
        titleSuffix={
          <ContextualHelpWithButton tracking="help: Enter your policy number?">
            <Typography>{getJSXContent('contextualhelpers.policyNumber.description')}</Typography>
          </ContextualHelpWithButton>
        }
        subtitle={getTextContent('fields.policyNumber.description')}
      />

      <ErrorMessage policy={policy} />
      {policyNotFound && (
        <>
          <Spacer size="md" />
          <Alert variant="error" heading="">
            {getTextContent('fields.policyNumber.notFound')}
          </Alert>
        </>
      )}
      <FormBlock preset="input" maxWidth="100%">
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              id="policynumber"
              ref={null}
              label={getTextContent('fields.policyNumber.label')}
              data-track-value="mask"
              data-track={getTextContent('fields.policyNumber.label')}
              error={!!fieldState.error}
              displayIcon
              helperText={
                fieldState.error
                  ? getTextContent('fields.policyNumber.invalid')
                  : getTextContent('fields.policyNumber.helperText')
              }
              type="text"
              required
              maxLength={12}
              InputLabelProps={{ required: false }}
              disabled={
                formState.isSubmitting || paymentState === 'retrieved' || paymentState === 'paying'
              }
            />
          )}
          name="reference"
          rules={{
            required: true,
            pattern: /^DSU(\d){6}BPK$/i,
          }}
        />
      </FormBlock>

      {(paymentState === 'init' || paymentState === 'retry') && (
        <FormBlock preset="button">
          <ButtonGroup
            primaryBtnProps={{
              type: 'submit',
              label: getTextContent('buttons.findPolicy'),
              disabled: !formState.isValid || formState.isSubmitting,
              'data-track': 'Update - payment reference',
            }}
            secondaryBtnProps={{
              label: getSharedContent('backButtonLabel'),
              'data-track': 'Exit - payment reference',
              onClick: onExit,
            }}
          />
          <Spinner
            description={getTextContent('fields.loadingLabel.searching')}
            loading={formState.isSubmitting && !hasError}
          />
        </FormBlock>
      )}
    </Form>
  );
};
